import { C } from '../../../../lib/design';
import { Ic, ICON_PATHS } from '../../../../lib/ui';
import { useI18n } from '../../../../i18n';
import { Section, EmptyState, InlineEdit, InlineSelect, AddItemRow } from './ProcessSheetHelpers';
import type { ReporteItem, RequisitoItem, FichaEditHook } from './ProcessSheetHelpers';

interface ProcessSheetReportsReqsProps {
  reportes: FichaEditHook<ReporteItem>;
  requisitos: FichaEditHook<RequisitoItem>;
  editMode: boolean;
  addingSection: string | null;
  setAddingSection: (v: string | null) => void;
  newItemValue: string;
  setNewItemValue: (v: string) => void;
  onAdd: (
    section: string,
    hook: { crear: (d: Record<string, unknown>) => Promise<unknown> },
  ) => void;
}

export function ProcessSheetReportsReqs({
  reportes,
  requisitos,
  editMode,
  addingSection,
  setAddingSection,
  newItemValue,
  setNewItemValue,
  onAdd,
}: ProcessSheetReportsReqsProps) {
  const { t } = useI18n();

  return (
    <Section
      icon={<Ic d={ICON_PATHS.chart} s={16} />}
      title={`4. ${t.processSheet.sections.reportsRequirements}`}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: C.azul2,
              textTransform: 'uppercase',
              marginBottom: 8,
            }}
          >
            {t.processSheet.fields.reports}
          </div>
          {reportes.items.length === 0 && !reportes.loading ? (
            <EmptyState message={t.processSheet.noData} />
          ) : (
            reportes.items.map((item) => (
              <div
                key={item.id}
                style={{
                  padding: '6px 0',
                  borderBottom: `1px solid ${C.borderAlt}`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <InlineEdit
                    value={item.nombre}
                    onSave={(v) => reportes.actualizar(item.id, { nombre: v })}
                    editMode={editMode}
                  />
                  {!editMode && item.frecuencia && (
                    <span
                      style={{
                        fontSize: 10,
                        color: C.textMut,
                        padding: '1px 6px',
                        borderRadius: 4,
                        background: `${C.azul2}10`,
                      }}
                    >
                      {item.frecuencia}
                    </span>
                  )}
                  {editMode && (
                    <button
                      onClick={() => reportes.eliminar(item.id)}
                      style={{
                        border: 'none',
                        background: 'none',
                        color: '#EF4444',
                        cursor: 'pointer',
                        fontSize: 14,
                        padding: 2,
                      }}
                    >
                      x
                    </button>
                  )}
                </div>
                {editMode && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 4, paddingLeft: 2 }}>
                    <InlineEdit
                      value={item.frecuencia ?? ''}
                      onSave={(v) => reportes.actualizar(item.id, { frecuencia: v || null })}
                      editMode={editMode}
                      placeholder={t.processSheet.placeholders.frecuencia}
                      style={{ fontSize: 11, color: C.textMut, flex: 1 }}
                    />
                    <InlineEdit
                      value={item.destinatario ?? ''}
                      onSave={(v) => reportes.actualizar(item.id, { destinatario: v || null })}
                      editMode={editMode}
                      placeholder={t.processSheet.placeholders.destinatario}
                      style={{ fontSize: 11, color: C.textMut, flex: 1 }}
                    />
                  </div>
                )}
              </div>
            ))
          )}
          {editMode && (
            <AddItemRow
              sectionKey="reportes"
              addingSection={addingSection}
              setAddingSection={setAddingSection}
              newItemValue={newItemValue}
              setNewItemValue={setNewItemValue}
              onAdd={() => onAdd('reportes', reportes)}
              addLabel={t.processSheet.addItem}
            />
          )}
        </div>
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: C.azul2,
              textTransform: 'uppercase',
              marginBottom: 8,
            }}
          >
            {t.processSheet.fields.requirements}
          </div>
          {requisitos.items.length === 0 && !requisitos.loading ? (
            <EmptyState message={t.processSheet.noData} />
          ) : (
            requisitos.items.map((item) => (
              <div
                key={item.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 0',
                  borderBottom: `1px solid ${C.borderAlt}`,
                }}
              >
                <InlineEdit
                  value={item.descripcion}
                  onSave={(v) => requisitos.actualizar(item.id, { descripcion: v })}
                  editMode={editMode}
                />
                <InlineSelect
                  value={item.tipo ?? ''}
                  options={[
                    { value: 'Legal', label: 'Legal' },
                    { value: 'Normativo', label: 'Normativo' },
                    { value: 'Contractual', label: 'Contractual' },
                    { value: 'Tecnico', label: 'Tecnico' },
                    { value: 'Cliente', label: 'Cliente' },
                    { value: 'Otro', label: 'Otro' },
                  ]}
                  onSave={(v) => requisitos.actualizar(item.id, { tipo: v || null })}
                  editMode={editMode}
                />
                {editMode && (
                  <button
                    onClick={() => requisitos.eliminar(item.id)}
                    style={{
                      border: 'none',
                      background: 'none',
                      color: '#EF4444',
                      cursor: 'pointer',
                      fontSize: 14,
                      padding: 2,
                    }}
                  >
                    x
                  </button>
                )}
              </div>
            ))
          )}
          {editMode && (
            <AddItemRow
              sectionKey="requisitos"
              addingSection={addingSection}
              setAddingSection={setAddingSection}
              newItemValue={newItemValue}
              setNewItemValue={setNewItemValue}
              onAdd={() => onAdd('requisitos', requisitos)}
              addLabel={t.processSheet.addItem}
            />
          )}
        </div>
      </div>
    </Section>
  );
}
